import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Spacing, Text } from '@reservamos/elements';
import Header from '@/components/Header';
import BrandLogo from '@/components/BrandLogo';
import CompleteHeader from '@/ui/atoms/CompleteHeader';
import PurchaseLayout from '@/ui/layouts/PurchaseLayout';
import PurchasePricing from '@/components/purchase/PurchasePricing';
import HelpContact from '@/components/purchase/HelpContact';
import 'styles/components/purchase/PurchasesLayout';

function consultationKey(token, paymentId) {
  return `mercadopago_bt_manual_read:${encodeURIComponent(token)}:${encodeURIComponent(paymentId)}`;
}

function canConsult(key) {
  try {
    return sessionStorage.getItem(key) === null;
  } catch {
    return false;
  }
}

/** Consult the same payment once more; never authorize another charge. */
function MercadoPagoBasisTheoryPending({ onVerify, token, paymentId, checking }) {
  const { t } = useTranslation('payment');
  const { t: purchaseText } = useTranslation('purchase');
  const purchase = useSelector((state) => state.purchase);
  const provider = useSelector((state) => state.whitelabelConfig.copies.provider);
  // A late response or a cold reload must not display another purchase's summary.
  const hasPurchase = purchase.get('token') === token && purchase.get('loaded');
  const key = consultationKey(token, paymentId);
  const [consumed, setConsumed] = useState(false);
  const available = !consumed && canConsult(key);
  const verify = () => {
    if (checking || !canConsult(key)) return;
    try {
      sessionStorage.setItem(key, 'used');
    } catch {
      setConsumed(true);
      return;
    }
    setConsumed(true);
    onVerify();
  };
  let message = 'mercadopago_bt_pending_verification';
  if (checking) message = 'mercadopago_bt_checking';
  else if (!available) message = 'mercadopago_bt_contact_support';
  return (
    <div className="l-complete">
      <Header isFixed hideLoyalty>
        <BrandLogo />
      </Header>
      <CompleteHeader
        title={t('payment_status', { context: checking ? 'pendingCompleted' : 'process' })}
        status={checking ? 'pendingCompleted' : 'process'}
        total={hasPurchase ? Number(purchase.get('total')) : 0}
      />
      <PurchaseLayout
        sidebar={
          <Spacing vertical size="L">
            <Text size="L" weight="bold">
              {purchaseText('label.purchase_details')}
            </Text>
            {hasPurchase && <PurchasePricing />}
            <Text>{t('mercadopago_bt_reference', { reference: token })}</Text>
          </Spacing>
        }
      >
        <Spacing vertical size="L">
          <div role="status" aria-live="polite" aria-busy={checking}>
            <Text size="L">{t(message)}</Text>
          </div>
          {!checking && available && (
            <Button onClick={verify} text={t('mercadopago_bt_verify_payment')} variant="primary" />
          )}
          <HelpContact
            email={provider.email}
            phones={provider.phones || []}
            whatsapp={provider.whatsapp || []}
          />
        </Spacing>
      </PurchaseLayout>
    </div>
  );
}

MercadoPagoBasisTheoryPending.propTypes = {
  onVerify: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  paymentId: PropTypes.string.isRequired,
  checking: PropTypes.bool,
};
MercadoPagoBasisTheoryPending.defaultProps = { checking: false };

export default MercadoPagoBasisTheoryPending;
